<script lang="ts">
import { defineComponent, PropType, ref, watch, onMounted } from 'vue'
import MultiSelectorDefault from '@/components/form/MultiSelectorDefault.vue'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'MultiSelectorWithExcludeOption',

  components: {
    MultiSelectorDefault,
  },

  props: {
    /**
     * <select> 要素の valueを受け取る
     * 値が null の場合は value 指定なしとして内部データで処理を行う
     */
    modelValue: {
      type: Array as PropType<MultiSelect>,
      default: () => [],
    },

    /**
     * <select> 要素の <option> 部分に渡す配列
     *
     * @description
     *  マルチセレクト自体は、単なる string 配列や number 配列も許容しているが、
     *  排他制御ありの本コンポーネントの場合、それらは許容しない。（isExclude オプションがある SelectItem 型のみ）
     */
    items: {
      type: Array as PropType<SelectItem[]>,
      required: true,
    },

    /**
     * フォームの name 属性
     * 任意プロパティ
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * <option> の末尾に付与される単位
     * 任意プロパティ
     *
     * @example unit="個" => <option>1個</option>
     */
    unit: {
      type: String,
      default: '',
    },

    /**
     * セレクターの非活性フラグ
     * 任意プロパティ
     *
     * @usage 活性状態を管理する親の data を受け渡す
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * エラー発生状態かどうか
     * ※ エラー状態は外から受け取る
     */
    isError: {
      type: Boolean,
      default: false,
    },

    /**
     * 選択のリセットボタンを表示するかどうか
     */
    hasReset: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'change'],

  setup(props, { emit }) {
    /**
     * コンポーネント内部でもつ選択状態の値
     * 親コンポーネントから value プロパティ を受け取らない場合は、このプロパティの値が selector に反映される
     */
    const localValue: Ref<MultiSelect> = ref([])

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    onMounted(() => {
      localValue.value = props.modelValue
    })

    /**
     * 指定の value の項目を抽出する
     *
     * @param value 項目の value 値
     */
    const findItem = (value: string | number): SelectItem | undefined => {
      return props.items.find(v => v.value === value) || undefined
    }

    /**
     * 選択された際の発火イベント
     * @param value 選択された項目の value
     * @param target 選択された項目の DOM
     *
     * @description
     *  Vue における emit の引数は暗黙的に受け取れます。
     *  複数の引数を受け取る場合、template 側の受け取り側は、明示的に `$event` を指定せず、暗黙的に受け取ることで複数キャッチすることができます。
     */
    const onChange = (value: string | number, target: HTMLInputElement) => {
      /**
       * 一旦 localValue ＝ 現在選択されている項目のうち、排他制御フラグが立っていないもののみを抽出
       *
       * @description
       *  排他制御フラグの項目のみが現在選択されている状態で、
       *  新しく項目が選択された場合、元々選択されていた「排他制御フラグの項目」の選択を外すため
       */
      const items = props.items.filter(v => {
        return (localValue.value as ReadonlyArray<string | number>).includes(v.value as string | number) && !v.isExclude
      }).map(v => v.value)

      localValue.value = items as string[] | number[]

      const isExclude = findItem(value)?.isExclude || false

      // 直前のアクションが「選択」で、且つ選択された項目の排他制御フラグが立っている場合は localValue の内容をその選択肢の値だけに書き換え
      if (isExclude && target.checked) {
        localValue.value = [value] as MultiSelect
      }
      emit('change', value, target)
      emit('update:modelValue', localValue.value)
    }

    /**
     * 更新イベント
     *
     * @param value emit で子コンポーネントから暗黙的に渡される値（選択されている値の配列）
     * @description
     *  基本は onChange のイベントで emit されるが、
     *  onChange が発火しない update イベント（＝ 選択解除等）用に update でも emit させる
     */
    const onUpdate = (value: string[] | number[]) => {
      localValue.value = value
      emit('update:modelValue', localValue.value)
    }

    return {
      // data
      localValue,

      // methods
      onUpdate,
      onChange,
    }
  }
})
</script>

<template>
  <MultiSelectorDefault
    :model-value="localValue"
    :items="items"
    :name="name"
    :unit="unit"
    :is-disabled="isDisabled"
    :is-error="isError"
    :has-reset="true"
    @update:model-value="onUpdate"
    @change="onChange" />
</template>
